import * as echarts from 'echarts'
import 'echarts-gl'
import { useEffect, useRef, useState } from 'preact/hooks'
import { basis, domain } from '../../../state/globalSignals.js'

// COMPARE_COLORS: Color scheme for multi-cell visualization (max 5 cells)
export const COMPARE_COLORS = ['#ea5b0c', '#0d7c99', '#88a2aa', '#ff8c42', '#0a4d68']

// Load points of the lower 2D plot (Z vs ambient temperature). Same four
// conditions as the single-cell views; one is shown at a time so the plot
// keeps one line per cell.
const LOAD_POINTS = [
  { key: 'c10', label: 'C/10' },
  { key: 'dc_25', label: 'Standard' },
  { key: 'cont', label: 'Continuous' },
  { key: 'peak', label: 'Peak' }
]
const LOAD_POINT_CATEGORIES = ['energy', 'power', 'heat', 'temperature', 'efficiency']

// Raw (absolute) load-point power/current from the normalized cell data
function getLoadPointsRaw(cellData, usePower) {
  const e = cellData?.electrical || {}
  return usePower
    ? { c10: e.P_dc_C10, dc_25: e.P_dc_25_W ?? e.P_dc_25, cont: e.P_dc_cont, peak: e.P_dc_peak }
    : { c10: e.I_dc_C10, dc_25: e.I_dc_25_A ?? e.I_dc_25, cont: e.I_dc_cont, peak: e.I_dc_peak }
}

// === MATLAB-STYLE HELPER: Delaunay Triangulation ===
// Implements 2D Delaunay triangulation for scattered points (Bowyer-Watson algorithm)
function delaunay2D(points) {
  if (points.length < 3) return []

  // Normalize points to [0, 1] range for numerical stability
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
  points.forEach(p => {
    minX = Math.min(minX, p.x)
    maxX = Math.max(maxX, p.x)
    minY = Math.min(minY, p.y)
    maxY = Math.max(maxY, p.y)
  })

  const dx = maxX - minX || 1
  const dy = maxY - minY || 1

  const normalizedPoints = points.map(p => ({
    x: (p.x - minX) / dx,
    y: (p.y - minY) / dy
  }))

  // Super-triangle far outside normalized bounds
  const superTriangle = [
    { x: -10, y: -10 },
    { x: 0.5, y: 20 },
    { x: 20, y: -10 }
  ]

  const allPoints = [...normalizedPoints, ...superTriangle]
  const n = normalizedPoints.length

  let triangles = [{ a: n, b: n + 1, c: n + 2 }]

  const inCircumcircle = (px, py, tri) => {
    const ax = allPoints[tri.a].x, ay = allPoints[tri.a].y
    const bx = allPoints[tri.b].x, by = allPoints[tri.b].y
    const cx = allPoints[tri.c].x, cy = allPoints[tri.c].y

    const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by))
    if (Math.abs(d) < 1e-14) return false

    const ux = ((ax*ax + ay*ay) * (by - cy) + (bx*bx + by*by) * (cy - ay) + (cx*cx + cy*cy) * (ay - by)) / d
    const uy = ((ax*ax + ay*ay) * (cx - bx) + (bx*bx + by*by) * (ax - cx) + (cx*cx + cy*cy) * (bx - ax)) / d
    const r2 = (ax - ux) * (ax - ux) + (ay - uy) * (ay - uy)
    const dist2 = (px - ux) * (px - ux) + (py - uy) * (py - uy)

    return dist2 < r2 + 1e-10
  }

  for (let i = 0; i < n; i++) {
    const px = normalizedPoints[i].x, py = normalizedPoints[i].y

    const badTriangles = []
    const goodTriangles = []

    triangles.forEach(tri => {
      if (inCircumcircle(px, py, tri)) {
        badTriangles.push(tri)
      } else {
        goodTriangles.push(tri)
      }
    })

    const edges = []
    badTriangles.forEach(tri => {
      edges.push({ a: tri.a, b: tri.b })
      edges.push({ a: tri.b, b: tri.c })
      edges.push({ a: tri.c, b: tri.a })
    })

    const boundaryEdges = edges.filter((e1, idx) => {
      return !edges.some((e2, idx2) => {
        if (idx === idx2) return false
        return (e1.a === e2.b && e1.b === e2.a) || (e1.a === e2.a && e1.b === e2.b)
      })
    })

    boundaryEdges.forEach(edge => {
      goodTriangles.push({ a: edge.a, b: edge.b, c: i })
    })

    triangles = goodTriangles
  }

  return triangles.filter(tri => tri.a < n && tri.b < n && tri.c < n)
}

/**
 * DimensionViewMultiCell - Multi-cell visualization component
 *
 * Renders electrical AND thermal property comparisons across multiple cells:
 * - Electrical: energy, power, voltage, ac-impedance, dc-resistance
 * - Thermal: heat, temperature, efficiency
 * - 1D: Side-by-side KPI cards with color-coded cells
 * - 2D: Multi-line charts with slices at X0 and Y0
 *   - AC Impedance: Nyquist plot (2D mode)
 * - 3D: Multi-surface plots
 *   - AC Impedance: Bode plots (3D mode)
 *
 * @param {Array} cellDataArray - Array of {productId, manufacturer, name, data}
 * @param {String} category - Category name (e.g., 'energy', 'power', 'voltage', 'heat', 'temperature')
 * @param {String} dimension - '1d', '2d', or '3d'
 */
export default function DimensionViewMultiCell({ cellDataArray, category, dimension = '1d' }) {
  const plot2dRef1 = useRef(null)
  const plot2dRef2 = useRef(null)
  const plot3dRef = useRef(null)

  const chartInstance1 = useRef(null)
  const chartInstance2 = useRef(null)
  const chart3DInstance = useRef(null)

  const [loadPoint, setLoadPoint] = useState('dc_25')
  const hasLoadPoints = LOAD_POINT_CATEGORIES.includes(category)

  // Extract data for each cell using getDataForCategory
  console.log('[DimensionViewMultiCell] Component rendered:', { category, dimension, cellCount: cellDataArray?.length })

  const multiCellData = cellDataArray?.map(cell => {
    const data = getDataForCategory(cell.data, category)
    console.log(`[Data Extract] ${cell.name} (${category}):`, {
      hasX: !!data?.X,
      hasY: !!data?.Y,
      xLen: data?.X?.length,
      yLen: data?.Y?.length,
      xLabel: data?.xLabel,
      yLabel: data?.yLabel
    })
    return {
      ...cell,
      data
    }
  })

  // Helper: Get data for specific category
  function getDataForCategory(cellData, cat) {
    if (!cellData) return null

    const b = basis.value
    const d = domain.value

    // Category name mapping
    const categoryMap = {
      energy: {
        xKey: d === 'energy-power' ? 'E' : 'CAP',
        yKey: 'T',
        zKey: d === 'energy-power' ? 'P_peak' : 'I_peak',
        xLabel: d === 'energy-power' ? 'Energy' : 'Capacity',
        yLabel: 'Ambient Temperature',
        zLabel: d === 'energy-power' ? 'Peak Power' : 'Peak Current',
        xUnit: b === 'abs' ? 'Wh' : (b === 'grav' ? 'Wh/kg' : 'Wh/L'),
        yUnit: '°C',
        zUnit: b === 'abs' ? 'W' : (b === 'grav' ? 'W/kg' : 'W/L')
      },
      power: {
        xKey: d === 'energy-power' ? 'P_peak' : 'I_peak',
        yKey: 'T',
        zKey: d === 'energy-power' ? 'E' : 'CAP',
        xLabel: d === 'energy-power' ? 'Peak Power' : 'Peak Current',
        yLabel: 'Ambient Temperature',
        zLabel: d === 'energy-power' ? 'Energy' : 'Capacity',
        xUnit: b === 'abs' ? 'W' : (b === 'grav' ? 'W/kg' : 'W/L'),
        yUnit: '°C',
        zUnit: b === 'abs' ? 'Wh' : (b === 'grav' ? 'Wh/kg' : 'Wh/L')
      },
      voltage: {
        xKey: 'V',
        yKey: 'T',
        zKey: 'R_dc',
        xLabel: 'Voltage',
        yLabel: 'Ambient Temperature',
        zLabel: 'DCIR',
        xUnit: 'V',
        yUnit: '°C',
        zUnit: 'mΩ'
      },
      resistance: {
        xKey: 'R_dc',
        yKey: 'T',
        zKey: d === 'energy-power' ? 'P_peak' : 'I_peak',
        xLabel: 'DCIR',
        yLabel: 'Ambient Temperature',
        zLabel: d === 'energy-power' ? 'Peak Power' : 'Peak Current',
        xUnit: 'mΩ',
        yUnit: '°C',
        zUnit: b === 'abs' ? 'W' : (b === 'grav' ? 'W/kg' : 'W/L')
      },
      lifespan: {
        xKey: 'cycles',
        yKey: 'T',
        zKey: 'CAP_loss_rel',
        xLabel: 'Cycle Count',
        yLabel: 'Ambient Temperature',
        zLabel: 'Capacity Loss',
        xUnit: 'cycles',
        yUnit: '°C',
        zUnit: '%'
      },
      thermal: {
        xKey: 'Q_loss',
        yKey: 'T',
        zKey: 'R_th',
        xLabel: 'Heat Loss',
        yLabel: 'Ambient Temperature',
        zLabel: 'Thermal Resistance',
        xUnit: 'W',
        yUnit: '°C',
        zUnit: 'K/W'
      },
      impedance: {
        xKey: 'f',
        yKey: 'Z_re',
        zKey: 'Z_im',
        xLabel: 'Frequency',
        yLabel: 'Real Impedance',
        zLabel: 'Imaginary Impedance',
        xUnit: 'Hz',
        yUnit: 'mΩ',
        zUnit: 'mΩ'
      },
      // === THERMAL CATEGORIES ===
      heat: {
        xKey: 'P_DCC',
        yKey: 'T_amb_DCC',
        zKey: 'P_loss_dcc_W',
        xLabel: 'Power',
        yLabel: 'Temperature',
        zLabel: 'Power Loss',
        xUnit: 'W',
        yUnit: '°C',
        zUnit: 'W'
      },
      temperature: {
        xKey: 'P_DCC',
        yKey: 'T_amb_DCC',
        zKey: 'T_max_dcc_C',
        xLabel: 'Power',
        yLabel: 'Ambient Temp',
        zLabel: 'Max Cell Temp',
        xUnit: 'W',
        yUnit: '°C',
        zUnit: '°C'
      },
      efficiency: {
        xKey: 'P_DCC',
        yKey: 'T_amb_DCC',
        zKey: 'eta_DCC',
        xLabel: 'Power',
        yLabel: 'Temperature',
        zLabel: 'Efficiency',
        xUnit: 'W',
        yUnit: '°C',
        zUnit: '%'
      }
    }

    const config = categoryMap[cat] || categoryMap.energy

    // Get mass and volume for basis scaling (same as single cell)
    const m_kg = cellData?.mechanical?.mass_g ? cellData.mechanical.mass_g / 1000 : null
    const vol_L = cellData?.mechanical?.vol_cell_cm3 ? cellData.mechanical.vol_cell_cm3 / 1000 : null

    let X = [], Y = [], Z = []

    let xLabel, yLabel, zLabel, xUnit, yUnit, zUnit
    let EIS_25_f_raw = []  // For AC impedance frequency data

    // Map category to data arrays (matching DimensionView.jsx getDataForCategory)
    if (cat === 'energy') {
      // Energy: Power/Current (X) vs Temperature (Y) vs Energy/Capacity (Z)
      const W_DCC = cellData?.electrical_W_DCC || []  // Wh
      const C_DCC = cellData?.electrical?.C_DCC || []  // Ah
      const P_DCC = cellData?.electrical_P_DCC || []  // W
      const I_DCC = cellData?.electrical?.I_DCC || []  // A
      const T_amb_DCC = cellData?.electrical_T_amb_DCC || []  // °C

      const useEnergy = !d || d === 'energy-power'
      X = useEnergy ? [...P_DCC] : [...I_DCC]
      Y = [...T_amb_DCC]
      Z = useEnergy ? [...W_DCC] : [...C_DCC]

      // Labels before scaling
      xLabel = useEnergy ? 'Power' : 'Current'
      yLabel = 'Ambient Temperature'
      zLabel = useEnergy ? 'Energy' : 'Capacity'
      xUnit = useEnergy ? 'W' : 'A'
      yUnit = '°C'
      zUnit = useEnergy ? 'Wh' : 'Ah'

      // Apply basis scaling
      if (b === 'grav' && m_kg) {
        X = X.map(x => x / m_kg)
        Z = Z.map(z => z / m_kg)
        xUnit += '/kg'
        zUnit += '/kg'
      } else if (b === 'vol' && vol_L) {
        X = X.map(x => x / vol_L)
        Z = Z.map(z => z / vol_L)
        xUnit = useEnergy ? 'W/L' : 'A/L'
        zUnit = useEnergy ? 'Wh/L' : 'Ah/L'
      }

    } else if (cat === 'power') {
      // Power: Power/Current (X) vs Temperature (Y) vs Max Duration (Z)
      const t_max_DCC = cellData?.electrical_t_max_DCC || []  // seconds
      const P_DCC = cellData?.electrical_P_DCC || []  // W
      const I_DCC = cellData?.electrical?.I_DCC || []  // A
      const T_amb_DCC = cellData?.electrical_T_amb_DCC || []  // °C

      const usePower = !d || d === 'energy-power'
      X = usePower ? [...P_DCC] : [...I_DCC]
      Y = [...T_amb_DCC]
      Z = [...t_max_DCC]

      // Labels before scaling
      xLabel = usePower ? 'Power' : 'Current'
      yLabel = 'Ambient Temperature'
      zLabel = 'Max Duration'
      xUnit = usePower ? 'W' : 'A'
      yUnit = '°C'
      zUnit = 's'

      // Apply basis scaling to X only
      if (b === 'grav' && m_kg) {
        X = X.map(x => x / m_kg)
        xUnit += '/kg'
      } else if (b === 'vol' && vol_L) {
        X = X.map(x => x / vol_L)
        xUnit = usePower ? 'W/L' : 'A/L'
      }

    } else if (cat === 'voltage') {
      // Voltage: Charge (X) vs Voltage (Y) - NO Z dimension, 2D only
      const DCC_C10_Q = cellData?.electrical_DCC_C10_Q || []  // Ah
      const DCC_C10_V = cellData?.electrical_DCC_C10_V || []  // V

      X = [...DCC_C10_Q]  // Charge [Ah]
      Y = [...DCC_C10_V]  // Voltage [V]
      Z = []  // No Z dimension

      xLabel = 'Charge'
      yLabel = 'Voltage'
      zLabel = ''
      xUnit = 'Ah'
      yUnit = 'V'
      zUnit = ''

      // Include V_min and V_max for reference lines
      const V_min = cellData?.electrical?.V_min
      const V_max = cellData?.electrical?.V_max

      return { X, Y, Z, xLabel, yLabel, zLabel, xUnit, yUnit, zUnit, V_min, V_max }

    } else if (cat === 'heat' || cat === 'temperature' || cat === 'efficiency') {
      // === THERMAL CATEGORIES ===
      const P_DCC = cellData?.electrical_P_DCC || []  // W
      const T_amb_DCC = cellData?.electrical_T_amb_DCC || []  // °C

      X = [...P_DCC]
      Y = [...T_amb_DCC]

      if (cat === 'heat') {
        const P_loss_dcc_W = cellData?.thermal?.P_loss_dcc_W || []  // W
        Z = Array.isArray(P_loss_dcc_W) ? [...P_loss_dcc_W] : []

        xLabel = 'Power'
        yLabel = 'Ambient Temperature'
        zLabel = 'Power Loss'
        xUnit = 'W'
        yUnit = '°C'
        zUnit = 'W'

        // Apply basis scaling to X and Z
        if (b === 'grav' && m_kg) {
          X = X.map(x => x / m_kg)
          Z = Z.map(z => z / m_kg)
          xUnit = 'W/kg'
          zUnit = 'W/kg'
        } else if (b === 'vol' && vol_L) {
          X = X.map(x => x / vol_L)
          Z = Z.map(z => z / vol_L)
          xUnit = 'W/L'
          zUnit = 'W/L'
        }

      } else if (cat === 'temperature') {
        const T_max_dcc_C = cellData?.thermal?.T_max_dcc_C || []  // °C
        Z = Array.isArray(T_max_dcc_C) ? [...T_max_dcc_C] : []

        xLabel = 'Power'
        yLabel = 'Ambient Temp'
        zLabel = 'Max Cell Temp'
        xUnit = 'W'
        yUnit = '°C'
        zUnit = '°C'

        // Apply basis scaling to X only (temperature is absolute)
        if (b === 'grav' && m_kg) {
          X = X.map(x => x / m_kg)
          xUnit = 'W/kg'
        } else if (b === 'vol' && vol_L) {
          X = X.map(x => x / vol_L)
          xUnit = 'W/L'
        }

      } else if (cat === 'efficiency') {
        // Efficiency [%], as in the single-cell ThermalDimensionView
        const eta_DCC = cellData?.electrical?.eta_DCC || []
        Z = Array.isArray(eta_DCC) ? eta_DCC.map(eta => eta * 100) : []

        xLabel = 'Power'
        yLabel = 'Ambient Temperature'
        zLabel = 'Efficiency'
        xUnit = 'W'
        yUnit = '°C'
        zUnit = '%'

        // Apply basis scaling to X only (efficiency is relative)
        if (b === 'grav' && m_kg) {
          X = X.map(x => x / m_kg)
          xUnit = 'W/kg'
        } else if (b === 'vol' && vol_L) {
          X = X.map(x => x / vol_L)
          xUnit = 'W/L'
        }
      }

    } else if (cat === 'ac-impedance') {
      // AC Impedance: Nyquist (2D) or Bode (3D)
      const EIS_25_Z_real = cellData?.electrical?.EIS_25_Z_real || []
      const EIS_25_Z_imag = cellData?.electrical?.EIS_25_Z_imag || []
      const EIS_25_f = cellData?.electrical?.EIS_25_f || []  // Frequency [Hz]

      X = [...EIS_25_Z_real]  // Real impedance [mΩ]
      Y = EIS_25_Z_imag.map(z => -z)  // Negative imaginary (inverted Y-axis) [mΩ]
      Z = []  // No Z for Nyquist, not used for Bode

      xLabel = "Z'{sub|real}"
      yLabel = "-Z''{sub|imag}"
      zLabel = ''
      xUnit = 'mΩ'
      yUnit = 'mΩ'
      zUnit = ''

      // Store frequency for Bode plots
      EIS_25_f_raw = EIS_25_f

    } else if (cat === 'dc-resistance') {
      // DCIR: SOC (X) vs Temperature (Y) vs Resistance (Z)
      const R_10_DCP = cellData?.electrical?.R_10_DCP || []
      const SOC_DCP = cellData?.electrical?.SOC_DCP || []
      const T_amb_DCP = cellData?.electrical?.T_amb_DCP || []

      X = SOC_DCP.map(soc => soc * 100)  // Convert to %
      Y = [...T_amb_DCP]  // °C
      Z = [...R_10_DCP]  // mΩ

      xLabel = 'SOC'
      yLabel = 'Ambient Temperature'
      zLabel = 'Resistance'
      xUnit = '%'
      yUnit = '°C'
      zUnit = 'mΩ'
    }

    // Load-point X values (C/10, Standard, Continuous, Peak) for the lower
    // plot, scaled exactly like X above
    let loadPointX = null
    if (LOAD_POINT_CATEGORIES.includes(cat)) {
      const usePower = (cat === 'energy' || cat === 'power') ? (!d || d === 'energy-power') : true
      const scale = (b === 'grav' && m_kg) ? m_kg : ((b === 'vol' && vol_L) ? vol_L : 1)
      const raw = getLoadPointsRaw(cellData, usePower)
      loadPointX = Object.fromEntries(
        Object.entries(raw).map(([k, v]) => [k, v ? v / scale : null])
      )
    }

    // Calculate reference points (X0, Y0) for slicing
    let X0 = X.length > 0 ? X[Math.floor(X.length / 2)] : 0
    // Temperature axes are always sliced at 25 °C, as in the single-cell
    // DimensionView. Picking the middle array element instead lands on 0 °C
    // for cells measured at an extra low temperature (e.g. Molicel P50B).
    const Y0 = yUnit === '°C' ? 25 : (Y.length > 0 ? Y[Math.floor(Y.length / 2)] : 25)

    // For dc-resistance, use 80% SOC as reference
    if (cat === 'dc-resistance') {
      X0 = 80
    }

    console.log(`[DimensionViewMultiCell] getDataForCategory('${cat}'):`, {
      xLabel, yLabel, zLabel,
      xUnit, yUnit, zUnit,
      xLength: X.length,
      yLength: Y.length,
      zLength: Z.length,
      X0, Y0
    })

    return {
      X, Y, Z, X0, Y0, loadPointX,
      xLabel, yLabel, zLabel,
      xUnit, yUnit, zUnit,
      EIS_25_f: EIS_25_f_raw || []  // For Bode plots
    }
  }

  // Render 2D plots when dimension or category changes
  useEffect(() => {
    if (!multiCellData || multiCellData.length === 0) return

    // AC Impedance and Voltage always use 2D rendering (even in "3D" mode for Bode)
    if (category === 'ac-impedance' || category === 'voltage') {
      console.log('[DimensionViewMultiCell] Triggering 2D render for category:', category)
      render2DPlotsMultiCell(multiCellData, category)
      return
    }

    // Normal 2D rendering for other categories
    if (dimension === '2d') {
      console.log('[DimensionViewMultiCell] Triggering 2D render for category:', category)
      render2DPlotsMultiCell(multiCellData, category)
    }
  }, [dimension, multiCellData, category, loadPoint])

  // Render 3D plot when dimension or category changes (skip for AC impedance and voltage)
  useEffect(() => {
    if (!multiCellData || multiCellData.length === 0) return

    // Skip 3D rendering for AC impedance and voltage (they use 2D ECharts)
    if (category === 'ac-impedance' || category === 'voltage') return

    if (dimension === '3d') {
      console.log('[DimensionViewMultiCell] Triggering 3D render for category:', category)
      render3DPlotMultiCell(multiCellData, category)
    }
  }, [dimension, multiCellData, category])

  // Cleanup charts on unmount
  useEffect(() => {
    return () => {
      if (chartInstance1.current) chartInstance1.current.dispose()
      if (chartInstance2.current) chartInstance2.current.dispose()
      if (chart3DInstance.current) chart3DInstance.current.dispose()
    }
  }, [])

  // ==================== MULTI-CELL RENDERING FUNCTIONS ====================

  function render2DPlotsMultiCell(cellsData, categoryName) {
    console.log('==============================================')
    console.log('[render2DPlotsMultiCell] CALLED')
    console.log('Category:', categoryName)
    console.log('Cell count:', cellsData?.length)
    console.log('Has plot2dRef1:', !!plot2dRef1.current)
    console.log('Has plot2dRef2:', !!plot2dRef2.current)
    console.log('==============================================')

    // For voltage and ac-impedance, only first plot ref is needed (single plot layout)
    const needsBothRefs = categoryName !== 'voltage' && categoryName !== 'ac-impedance'

    if (!plot2dRef1.current) {
      console.error('[render2DPlotsMultiCell] ❌ MISSING plot2dRef1!')
      return
    }

    if (needsBothRefs && !plot2dRef2.current) {
      console.error('[render2DPlotsMultiCell] ❌ MISSING plot2dRef2 (needed for dual-plot category)!')
      return
    }
    if (!cellsData || cellsData.length === 0) {
      console.error('[render2DPlotsMultiCell] ❌ NO CELL DATA!')
      return
    }

    console.log(`=== Rendering 2D Multi-Cell ${categoryName} ===`, cellsData.length, 'cells')

    // ALWAYS dispose existing charts first to prevent stale data from previous category
    if (chartInstance1.current) {
      console.log('[DimensionViewMultiCell] Disposing chartInstance1')
      chartInstance1.current.dispose()
      chartInstance1.current = null
    }
    if (chartInstance2.current) {
      console.log('[DimensionViewMultiCell] Disposing chartInstance2')
      chartInstance2.current.dispose()
      chartInstance2.current = null
    }

    // Special case: Voltage only has one plot (V vs Q)
    if (categoryName === 'voltage') {
      // Hide second plot
      if (plot2dRef2.current) plot2dRef2.current.style.display = 'none'
    } else {
      // Show second plot for other categories
      if (plot2dRef2.current) plot2dRef2.current.style.display = 'block'
    }

    // Get first cell's data for axis setup
    const firstData = cellsData[0].data
    console.log(`[${categoryName}] First cell data:`, {
      hasX: !!firstData?.X,
      hasY: !!firstData?.Y,
      hasZ: !!firstData?.Z,
      xLength: firstData?.X?.length,
      yLength: firstData?.Y?.length,
      xLabel: firstData?.xLabel,
      yLabel: firstData?.yLabel
    })
    if (!firstData || !firstData.X || !firstData.Y) {
      console.warn(`[${categoryName}] Missing data - X:`, !!firstData?.X, 'Y:', !!firstData?.Y)
      return
    }

    // Special rendering for voltage (single plot: V vs Q)
    if (categoryName === 'voltage') {
      console.log('[VOLTAGE] Starting voltage plot rendering')
      chartInstance1.current = echarts.init(plot2dRef1.current)
      console.log('[VOLTAGE] ECharts instance created')

      // Collect all voltage data points and V_min/V_max values for axis range
      let allVoltages = []
      let allVMin = []
      let allVMax = []

      const voltageSeries = cellsData.map((cell, idx) => {
        const d = cell.data
        const color = COMPARE_COLORS[idx % COMPARE_COLORS.length]
        const cellName = `${cell.manufacturer || ''} ${cell.name || ''}`.trim()

        console.log(`[VOLTAGE] Creating series for ${cellName}:`, { xLength: d.X?.length, yLength: d.Y?.length })
        const voltageData = d.X.map((q, i) => [q, d.Y[i]])
        console.log(`[VOLTAGE] Voltage data points:`, voltageData.length, 'first:', voltageData[0])

        // Collect voltages for axis range
        allVoltages = allVoltages.concat(d.Y)
        if (d.V_min != null) allVMin.push(d.V_min)
        if (d.V_max != null) allVMax.push(d.V_max)

        return {
          type: 'line',
          name: cellName,
          data: voltageData,
          lineStyle: { color, width: 3 },
          symbol: 'none',
          itemStyle: { color },
          smooth: false
        }
      })

      // Calculate Y-axis range including V_min and V_max reference lines
      const dataMinV = Math.min(...allVoltages)
      const dataMaxV = Math.max(...allVoltages)
      const globalVMin = allVMin.length > 0 ? Math.min(...allVMin) : dataMinV
      const globalVMax = allVMax.length > 0 ? Math.max(...allVMax) : dataMaxV

      const minV = Math.min(dataMinV, globalVMin)
      const maxV = Math.max(dataMaxV, globalVMax)
      const vRange = maxV - minV
      const yMin = Math.max(0, minV - vRange * 0.1) // 10% padding below
      const yMax = maxV + vRange * 0.1 // 10% padding above

      // Add V_min and V_max reference lines as markLines
      const markLineSeries = []
      if (allVMin.length > 0) {
        markLineSeries.push({
          type: 'line',
          markLine: {
            silent: true,
            symbol: 'none',
            label: {
              show: true,
              position: 'insideEndBottom',
              formatter: 'V_min',
              color: '#999',
              fontSize: 11,
              fontFamily: 'Roboto'
            },
            lineStyle: { type: 'dashed', color: '#999', width: 1.5 },
            data: [{ yAxis: globalVMin }]
          }
        })
      }
      if (allVMax.length > 0) {
        markLineSeries.push({
          type: 'line',
          markLine: {
            silent: true,
            symbol: 'none',
            label: {
              show: true,
              position: 'insideEndTop',
              formatter: 'V_max',
              color: '#999',
              fontSize: 11,
              fontFamily: 'Roboto'
            },
            lineStyle: { type: 'dashed', color: '#999', width: 1.5 },
            data: [{ yAxis: globalVMax }]
          }
        })
      }

      chartInstance1.current.setOption({
        backgroundColor: 'transparent',
        grid: { left: 70, right: 30, top: 50, bottom: 70 },
        legend: {
          show: true,
          top: 10,
          textStyle: { color: '#cfd2d6', fontFamily: 'Roboto', fontSize: 11 }
        },
        xAxis: {
          type: 'value',
          name: `${firstData.xLabel} (${firstData.xUnit})`,
          nameLocation: 'middle',
          nameGap: 40,
          nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
          axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
          axisLabel: { color: '#999', fontFamily: 'Roboto', fontSize: 11 },
          splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
        },
        yAxis: {
          type: 'value',
          name: `${firstData.yLabel} (${firstData.yUnit})`,
          nameLocation: 'middle',
          nameGap: 50,
          min: yMin,
          max: yMax,
          nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
          axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
          axisLabel: { color: '#999', fontFamily: 'Roboto', fontSize: 11 },
          splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
        },
        series: [...voltageSeries, ...markLineSeries],
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(45, 42, 38, 0.95)',
          borderColor: '#ea5b0c',
          textStyle: { color: '#fff', fontFamily: 'Roboto', fontSize: 12 }
        }
      })

      console.log('[VOLTAGE] Chart rendering complete')
      return // Exit early for voltage
    }

    // Special rendering for AC impedance: Nyquist (2D) or Bode (3D)
    if (categoryName === 'ac-impedance') {
      if (dimension === '2d') {
        // === NYQUIST PLOT: -Z" vs Z' ===
        console.log('[AC-IMPEDANCE] Starting Nyquist plot rendering')
        chartInstance1.current = echarts.init(plot2dRef1.current)

        const nyquistSeries = cellsData.map((cell, idx) => {
          const d = cell.data
          const color = COMPARE_COLORS[idx % COMPARE_COLORS.length]
          const cellName = `${cell.manufacturer || ''} ${cell.name || ''}`.trim()

          const nyquistData = d.X.map((zReal, i) => [zReal, d.Y[i]]) // X=Z_real, Y=-Z_imag

          return {
            type: 'line',
            name: cellName,
            data: nyquistData,
            lineStyle: { color, width: 3 },
            symbol: 'circle',
            symbolSize: 4,
            itemStyle: { color },
            smooth: false
          }
        })

        // Calculate axis ranges for equal aspect ratio (1:1 for Nyquist)
        let allXValues = []
        let allYValues = []
        cellsData.forEach(cell => {
          allXValues = allXValues.concat(cell.data.X)
          allYValues = allYValues.concat(cell.data.Y)
        })

        const xMin = Math.min(...allXValues)
        const xMax = Math.max(...allXValues)
        const yMin = Math.min(...allYValues)
        const yMax = Math.max(...allYValues)

        // Calculate actual data bounds with padding
        const padding = 0.05 // 5% padding
        const xDataRange = xMax - xMin
        const yDataRange = yMax - yMin
        const xPadding = xDataRange * padding
        const yPadding = yDataRange * padding

        const xDataMin = xMin - xPadding
        const xDataMax = xMax + xPadding
        const yDataMin = yMin - yPadding
        const yDataMax = yMax + yPadding

        // Calculate grid dimensions to enforce equal pixel scaling (axis equal)
        const containerWidth = plot2dRef1.current.offsetWidth
        const containerHeight = plot2dRef1.current.offsetHeight

        // Grid margins for labels
        const gridLeft = 70
        const gridRight = 30
        const gridTop = 50
        const gridBottom = 70

        const gridWidth = containerWidth - gridLeft - gridRight
        const gridHeight = containerHeight - gridTop - gridBottom

        // Calculate data range with padding
        const xRange = xDataMax - xDataMin
        const yRange = yDataMax - yDataMin

        // Calculate pixels per unit - use the more restrictive axis
        const pixelsPerUnitX = gridWidth / xRange
        const pixelsPerUnitY = gridHeight / yRange
        const pixelsPerUnit = Math.min(pixelsPerUnitX, pixelsPerUnitY)

        // Adjust axis ranges to fill grid while maintaining equal scaling
        const dataCenterX = (xDataMin + xDataMax) / 2
        const dataCenterY = (yDataMin + yDataMax) / 2

        const xAxisRange = gridWidth / pixelsPerUnit
        const yAxisRange = gridHeight / pixelsPerUnit

        const xAxisMin = dataCenterX - xAxisRange / 2
        const xAxisMax = dataCenterX + xAxisRange / 2
        const yAxisMin = dataCenterY - yAxisRange / 2
        const yAxisMax = dataCenterY + yAxisRange / 2

        // Calculate nice tick interval based on pixel spacing
        // Aim for ~100 pixels between ticks
        const targetPixelsPerTick = 100
        const dataUnitsPerTick = targetPixelsPerTick / pixelsPerUnit

        // Round to nice number (0.1, 0.2, 0.5, 1, 2, 5, 10, etc.)
        const magnitude = Math.pow(10, Math.floor(Math.log10(dataUnitsPerTick)))
        const normalized = dataUnitsPerTick / magnitude
        let niceInterval
        if (normalized < 1.5) niceInterval = 1 * magnitude
        else if (normalized < 3) niceInterval = 2 * magnitude
        else if (normalized < 7) niceInterval = 5 * magnitude
        else niceInterval = 10 * magnitude

        // Round axis limits to nice interval
        let xAxisMinRounded = Math.floor(xAxisMin / niceInterval) * niceInterval
        let xAxisMaxRounded = Math.ceil(xAxisMax / niceInterval) * niceInterval
        let yAxisMinRounded = Math.floor(yAxisMin / niceInterval) * niceInterval
        let yAxisMaxRounded = Math.ceil(yAxisMax / niceInterval) * niceInterval

        // After rounding, recalculate to maintain equal pixel scaling
        const xRangeRounded = xAxisMaxRounded - xAxisMinRounded
        const yRangeRounded = yAxisMaxRounded - yAxisMinRounded

        // Adjust the shorter range to match pixel scaling
        if (xRangeRounded / gridWidth < yRangeRounded / gridHeight) {
          // X range needs to be expanded
          const targetXRange = (yRangeRounded / gridHeight) * gridWidth
          const xExpand = (targetXRange - xRangeRounded) / 2
          xAxisMinRounded -= Math.ceil(xExpand / niceInterval) * niceInterval
          xAxisMaxRounded += Math.ceil(xExpand / niceInterval) * niceInterval
        } else {
          // Y range needs to be expanded
          const targetYRange = (xRangeRounded / gridWidth) * gridHeight
          const yExpand = (targetYRange - yRangeRounded) / 2
          yAxisMinRounded -= Math.ceil(yExpand / niceInterval) * niceInterval
          yAxisMaxRounded += Math.ceil(yExpand / niceInterval) * niceInterval
        }

        chartInstance1.current.setOption({
          backgroundColor: 'transparent',
          grid: {
            left: gridLeft,
            right: gridRight,
            top: gridTop,
            bottom: gridBottom,
            containLabel: false
          },
          legend: {
            show: true,
            top: 10,
            textStyle: { color: '#cfd2d6', fontFamily: 'Roboto', fontSize: 11 }
          },
          xAxis: {
            type: 'value',
            name: firstData.xLabel,
            nameLocation: 'middle',
            nameGap: 40,
            nameTextStyle: {
              color: '#cfd2d6',
              fontSize: 12,
              fontFamily: 'Roboto',
              rich: {
                sub: {
                  fontSize: 9,
                  verticalAlign: 'bottom',
                  padding: [3, 0, 0, 0]
                }
              }
            },
            axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
            axisLabel: {
              color: '#999',
              fontFamily: 'Roboto',
              fontSize: 11,
              formatter: (value) => value.toFixed(2)
            },
            splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } },
            min: xAxisMinRounded,
            max: xAxisMaxRounded,
            interval: niceInterval
          },
          yAxis: {
            type: 'value',
            name: firstData.yLabel,
            nameLocation: 'middle',
            nameGap: 50,
            nameTextStyle: {
              color: '#cfd2d6',
              fontSize: 12,
              fontFamily: 'Roboto',
              rich: {
                sub: {
                  fontSize: 9,
                  verticalAlign: 'bottom',
                  padding: [3, 0, 0, 0]
                }
              }
            },
            axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
            axisLabel: {
              color: '#999',
              fontFamily: 'Roboto',
              fontSize: 11,
              formatter: (value) => value.toFixed(2)
            },
            splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } },
            min: yAxisMinRounded,
            max: yAxisMaxRounded,
            interval: niceInterval
          },
          series: nyquistSeries,
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(45, 42, 38, 0.95)',
            borderColor: '#ea5b0c',
            textStyle: { color: '#fff', fontFamily: 'Roboto', fontSize: 12 }
          }
        })

        // Hide second plot
        if (plot2dRef2.current) plot2dRef2.current.style.display = 'none'

      } else {
        // === BODE PLOTS: Magnitude and Phase vs Frequency (3D mode) ===
        console.log('[AC-IMPEDANCE] Starting Bode plots rendering')

        // Show both plots for Bode
        if (plot2dRef2.current) plot2dRef2.current.style.display = 'block'

        // Build series for magnitude and phase plots
        const magnitudeSeries = []
        const phaseSeries = []

        cellsData.forEach((cell, idx) => {
          const d = cell.data
          const color = COMPARE_COLORS[idx % COMPARE_COLORS.length]
          const cellName = `${cell.manufacturer || ''} ${cell.name || ''}`.trim()

          // Calculate magnitude |Z| = sqrt(Z_real^2 + Z_imag^2) and phase
          const magnitudeData = []
          const phaseData = []

          for (let i = 0; i < d.EIS_25_f.length; i++) {
            const f = d.EIS_25_f[i]
            const zReal = d.X[i]  // Z_real
            const zImag = -d.Y[i]  // Original Z_imag (Y is already negated, so negate again)

            const magnitude = Math.sqrt(zReal * zReal + zImag * zImag)
            const phase = Math.atan2(zImag, zReal) * (180 / Math.PI)

            magnitudeData.push([f, magnitude])
            phaseData.push([f, phase])
          }

          magnitudeSeries.push({
            type: 'line',
            name: cellName,
            data: magnitudeData,
            showSymbol: false,
            lineStyle: { color, width: 3 }
          })

          phaseSeries.push({
            type: 'line',
            name: cellName,
            data: phaseData,
            showSymbol: false,
            lineStyle: { color, width: 3 }
          })
        })

        // PLOT 1: Magnitude vs Frequency
        chartInstance1.current = echarts.init(plot2dRef1.current)
        chartInstance1.current.setOption({
          backgroundColor: 'transparent',
          grid: { left: 70, right: 30, top: 50, bottom: 70 },
          legend: {
            show: true,
            top: 10,
            textStyle: { color: '#cfd2d6', fontFamily: 'Roboto', fontSize: 11 }
          },
          xAxis: {
            type: 'log',
            name: 'Frequency (Hz)',
            nameLocation: 'middle',
            nameGap: 40,
            nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
            axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
            axisLabel: { color: '#999', fontFamily: 'Roboto', fontSize: 11 },
            splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
          },
          yAxis: {
            type: 'value',
            name: '|Z| (mΩ)',
            nameLocation: 'middle',
            nameGap: 50,
            nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
            axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
            axisLabel: { color: '#999', fontFamily: 'Roboto', fontSize: 11 },
            splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
          },
          series: magnitudeSeries,
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(45, 42, 38, 0.95)',
            borderColor: '#ea5b0c',
            textStyle: { color: '#fff', fontFamily: 'Roboto', fontSize: 12 }
          }
        })

        // PLOT 2: Phase vs Frequency
        chartInstance2.current = echarts.init(plot2dRef2.current)
        chartInstance2.current.setOption({
          backgroundColor: 'transparent',
          grid: { left: 70, right: 30, top: 50, bottom: 70 },
          legend: {
            show: true,
            top: 10,
            textStyle: { color: '#cfd2d6', fontFamily: 'Roboto', fontSize: 11 }
          },
          xAxis: {
            type: 'log',
            name: 'Frequency (Hz)',
            nameLocation: 'middle',
            nameGap: 40,
            nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
            axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
            axisLabel: { color: '#999', fontFamily: 'Roboto', fontSize: 11 },
            splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
          },
          yAxis: {
            type: 'value',
            name: 'Phase (°)',
            nameLocation: 'middle',
            nameGap: 50,
            nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
            axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
            axisLabel: { color: '#999', fontFamily: 'Roboto', fontSize: 11 },
            splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
          },
          series: phaseSeries,
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(45, 42, 38, 0.95)',
            borderColor: '#0d7c99',
            textStyle: { color: '#fff', fontFamily: 'Roboto', fontSize: 12 }
          }
        })
      }

      return // Exit early for AC impedance
    }

    if (!firstData.Z) return

    // Initialize new chart instances
    chartInstance1.current = echarts.init(plot2dRef1.current)
    chartInstance2.current = echarts.init(plot2dRef2.current)

    // Build series for each cell (Chart 1: Z vs X at Y=Y0)
    const chart1Series = []
    const chart2Series = []

    cellsData.forEach((cell, idx) => {
      const d = cell.data
      const color = COMPARE_COLORS[idx % COMPARE_COLORS.length]
      const cellName = `${cell.manufacturer || ''} ${cell.name || ''}`.trim()

      // Chart 1: X-slice (Z vs X at Y≈Y0) - same logic as DimensionView.jsx
      const yTolerance = d.yUnit === '°C' ? 2 : Math.abs(d.Y0 * 0.2)
      const xSliceData = []
      for (let i = 0; i < d.X.length; i++) {
        if (Math.abs(d.Y[i] - d.Y0) <= yTolerance) {
          xSliceData.push({ x: d.X[i], z: d.Z[i] })
        }
      }

      // Sort by X and remove duplicates
      xSliceData.sort((a, b) => a.x - b.x)
      const xMap = new Map()
      xSliceData.forEach(pt => {
        const xKey = pt.x.toFixed(3)
        if (xMap.has(xKey)) {
          xMap.set(xKey, (xMap.get(xKey) + pt.z) / 2)
        } else {
          xMap.set(xKey, pt.z)
        }
      })
      const xSlice = Array.from(xMap.entries())
        .map(([xStr, z]) => [parseFloat(xStr), z])
        .sort((a, b) => a[0] - b[0])

      chart1Series.push({
        type: 'line',
        name: cellName,
        data: xSlice,
        lineStyle: { color, width: 3 },
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: { color },
        smooth: false
      })

      // Chart 2: Y-slice (Z vs Y) at the selected load point, with the same
      // tolerance as the single-cell views (1 % in DimensionView, 10 % in
      // ThermalDimensionView). Categories without load points slice at X0.
      const isThermal = ['heat', 'temperature', 'efficiency'].includes(categoryName)
      const sliceX = d.loadPointX ? d.loadPointX[loadPoint] : d.X0
      const xTolerance = d.loadPointX ? (isThermal ? 0.10 : 0.01) : 0.3
      const ySliceData = []
      for (let i = 0; sliceX && i < d.X.length; i++) {
        const xDiff = Math.abs(d.X[i] - sliceX) / Math.max(Math.abs(sliceX), 0.001)
        if (xDiff <= xTolerance) {
          ySliceData.push({ y: d.Y[i], z: d.Z[i] })
        }
      }

      // Sort by Y and remove duplicates
      ySliceData.sort((a, b) => a.y - b.y)
      const yMap = new Map()
      ySliceData.forEach(pt => {
        const yKey = d.yUnit === '°C' ? Math.round(pt.y * 10) / 10 : pt.y.toFixed(3)
        if (yMap.has(yKey)) {
          yMap.set(yKey, (yMap.get(yKey) + pt.z) / 2)
        } else {
          yMap.set(yKey, pt.z)
        }
      })
      const ySlice = Array.from(yMap.entries())
        .map(([yStr, z]) => [typeof yStr === 'string' ? parseFloat(yStr) : yStr, z])
        .sort((a, b) => a[0] - b[0])

      chart2Series.push({
        type: 'line',
        name: cellName,
        data: ySlice,
        lineStyle: { color, width: 3 },
        symbol: 'circle',
        symbolSize: 6,
        itemStyle: { color },
        smooth: false
      })
    })

    // Chart 1 Options
    chartInstance1.current.setOption({
      backgroundColor: 'transparent',
      grid: { left: 70, right: 30, top: 50, bottom: 70 },
      legend: {
        show: true,
        top: 10,
        textStyle: { color: '#cfd2d6', fontFamily: 'Roboto', fontSize: 11 }
      },
      xAxis: {
        type: 'value',
        name: `${firstData.xLabel} (${firstData.xUnit})`,
        nameLocation: 'middle',
        nameGap: 40,
        nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
        axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
        axisLabel: { color: '#999', fontFamily: 'Roboto', fontSize: 11 },
        splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
      },
      yAxis: {
        type: categoryName === 'power' ? 'log' : 'value',
        name: `${firstData.zLabel} (${firstData.zUnit})`,
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
        axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
        axisLabel: { color: '#999', fontFamily: 'Roboto', fontSize: 11 },
        splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
      },
      series: chart1Series,
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(45, 42, 38, 0.95)',
        borderColor: '#ea5b0c',
        textStyle: { color: '#fff', fontFamily: 'Roboto', fontSize: 12 }
      }
    })

    // Chart 2 Options
    chartInstance2.current.setOption({
      backgroundColor: 'transparent',
      grid: { left: 70, right: 30, top: 50, bottom: 70 },
      legend: {
        show: true,
        top: 10,
        textStyle: { color: '#cfd2d6', fontFamily: 'Roboto', fontSize: 11 }
      },
      xAxis: {
        type: 'value',
        name: `${firstData.yLabel} (${firstData.yUnit})`,
        nameLocation: 'middle',
        nameGap: 40,
        nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
        axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
        axisLabel: { color: '#999', fontFamily: 'Roboto', fontSize: 11 },
        splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
      },
      yAxis: {
        type: categoryName === 'power' ? 'log' : 'value',
        name: `${firstData.zLabel} (${firstData.zUnit})`,
        nameLocation: 'middle',
        nameGap: 50,
        nameTextStyle: { color: '#cfd2d6', fontSize: 12, fontFamily: 'Roboto' },
        axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
        axisLabel: { color: '#999', fontFamily: 'Roboto', fontSize: 11 },
        splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' } }
      },
      series: chart2Series,
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(45, 42, 38, 0.95)',
        borderColor: '#0d7c99',
        textStyle: { color: '#fff', fontFamily: 'Roboto', fontSize: 12 }
      }
    })
  }

  function render3DPlotMultiCell(cellsData, categoryName) {
    if (!plot3dRef.current) return
    if (!cellsData || cellsData.length === 0) return

    if (!window.Plotly) {
      console.warn('⚠️ Plotly not available for 3D plots')
      return
    }

    console.log(`=== Rendering 3D Multi-Cell ${categoryName} (mesh3d) ===`, cellsData.length, 'cells')

    // Build traces for each cell using mesh3d (like single cell page)
    const traces = []

    cellsData.forEach((cell, idx) => {
      const d = cell.data
      if (!d || !d.X || !d.Y || !d.Z || d.X.length === 0) {
        console.warn(`Cell ${idx} has no 3D data`)
        return
      }

      const cellName = `${cell.manufacturer || ''} ${cell.name || ''}`.trim()
      const color = COMPARE_COLORS[idx % COMPARE_COLORS.length]

      // Create scattered points for Delaunay triangulation
      const scatteredPoints = []
      for (let i = 0; i < d.X.length; i++) {
        scatteredPoints.push({ x: d.X[i], y: d.Y[i] })
      }

      // Delaunay triangulation
      const triangles = delaunay2D(scatteredPoints)

      // Flatten triangles into i, j, k arrays for Plotly mesh3d
      const meshI = [], meshJ = [], meshK = []
      triangles.forEach(tri => {
        meshI.push(tri.a)
        meshJ.push(tri.b)
        meshK.push(tri.c)
      })

      console.log(`Cell ${cellName}: ${d.X.length} points -> ${triangles.length} triangles`)

      // Create wireframe edges from triangles
      const edgeSet = new Set()
      const wireframeX = [], wireframeY = [], wireframeZ = []

      triangles.forEach(tri => {
        const edges = [
          [Math.min(tri.a, tri.b), Math.max(tri.a, tri.b)],
          [Math.min(tri.b, tri.c), Math.max(tri.b, tri.c)],
          [Math.min(tri.c, tri.a), Math.max(tri.c, tri.a)]
        ]

        edges.forEach(([i, j]) => {
          const key = `${i}-${j}`
          if (!edgeSet.has(key)) {
            edgeSet.add(key)
            wireframeX.push(d.X[i], d.X[j], null)
            wireframeY.push(d.Y[i], d.Y[j], null)
            wireframeZ.push(d.Z[i], d.Z[j], null)
          }
        })
      })

      // Mesh3d surface (like MATLAB's trisurf)
      traces.push({
        type: 'mesh3d',
        x: d.X,
        y: d.Y,
        z: d.Z,
        i: meshI,
        j: meshJ,
        k: meshK,
        intensity: d.Z,
        colorscale: [
          [0, color],
          [1, color]
        ],
        opacity: 0.7,
        showscale: false,
        flatshading: false,
        name: cellName,
        lighting: {
          ambient: 0.6,
          diffuse: 0.8,
          specular: 0.3,
          roughness: 0.5,
          fresnel: 0.2
        },
        lightposition: { x: 5000, y: 5000, z: 5000 },
        hovertemplate: `<b>${cellName}</b><br>` +
          `${d.xLabel}: %{x:.2f} ${d.xUnit}<br>` +
          `${d.yLabel}: %{y:.2f} ${d.yUnit}<br>` +
          `${d.zLabel}: %{z:.2f} ${d.zUnit}<extra></extra>`
      })

      // Wireframe edges (subtle grid lines)
      traces.push({
        type: 'scatter3d',
        mode: 'lines',
        name: `${cellName} grid`,
        x: wireframeX,
        y: wireframeY,
        z: wireframeZ,
        line: {
          color: `rgba(${hexToRgb(color)}, 0.3)`,
          width: 1
        },
        hoverinfo: 'skip',
        showlegend: false
      })

      // Data point markers
      traces.push({
        type: 'scatter3d',
        mode: 'markers',
        name: `${cellName} points`,
        x: d.X,
        y: d.Y,
        z: d.Z,
        marker: {
          size: 3,
          color: color,
          opacity: 0.8
        },
        hovertemplate: `<b>${cellName}</b><br>` +
          `${d.xLabel}: %{x:.2f} ${d.xUnit}<br>` +
          `${d.yLabel}: %{y:.2f} ${d.yUnit}<br>` +
          `${d.zLabel}: %{z:.2f} ${d.zUnit}<extra></extra>`,
        showlegend: false
      })
    })

    const firstData = cellsData[0].data
    const useLogZ = categoryName === 'power'

    const layout = {
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      scene: {
        xaxis: {
          title: `${firstData.xLabel} (${firstData.xUnit})`,
          titlefont: { color: '#cfd2d6', size: 12 },
          gridcolor: 'rgba(255, 255, 255, 0.1)',
          color: '#999'
        },
        yaxis: {
          title: `${firstData.yLabel} (${firstData.yUnit})`,
          titlefont: { color: '#cfd2d6', size: 12 },
          gridcolor: 'rgba(255, 255, 255, 0.1)',
          color: '#999'
        },
        zaxis: {
          title: `${firstData.zLabel} (${firstData.zUnit})`,
          titlefont: { color: '#cfd2d6', size: 12 },
          gridcolor: 'rgba(255, 255, 255, 0.1)',
          color: '#999',
          type: useLogZ ? 'log' : 'linear'
        },
        bgcolor: 'rgba(0, 0, 0, 0)',
        camera: {
          eye: { x: 1.5, y: -1.5, z: 1.3 }  // Rotated 90° CW from default
        }
      },
      margin: { l: 0, r: 0, t: 0, b: 0 },
      legend: {
        font: { color: '#cfd2d6', family: 'Roboto', size: 11 },
        bgcolor: 'rgba(45, 42, 38, 0.8)',
        bordercolor: 'rgba(255, 255, 255, 0.1)',
        borderwidth: 1
      },
      hovermode: 'closest'
    }

    const config = {
      responsive: true,
      displayModeBar: false,
      displaylogo: false
    }

    window.Plotly.newPlot(plot3dRef.current, traces, layout, config)
  }

  // Helper: Convert hex color to RGB string
  function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
      : '255, 255, 255'
  }

  // Helper: Extract slice at constant Y
  function getSliceAtY(X, Y, Z, Y0) {
    const result = []
    const tolerance = 2 // °C tolerance for temperature matching

    for (let i = 0; i < X.length; i++) {
      if (Math.abs(Y[i] - Y0) < tolerance) {
        result.push(Z[i])
      }
    }

    return result
  }

  // Helper: Extract slice at constant X
  function getSliceAtX(X, Y, Z, X0) {
    const result = []
    const tolerance = X0 * 0.1 // 10% tolerance for X matching

    for (let i = 0; i < X.length; i++) {
      if (Math.abs(X[i] - X0) < tolerance) {
        result.push(Z[i])
      }
    }

    return result
  }

  // ==================== END MULTI-CELL FUNCTIONS ====================

  // Removed old helper functions getSliceAtY and getSliceAtX
  // Slicing is now done inline in render2DPlotsMultiCell (matching DimensionView.jsx logic)

  // Get KPIs for 1D view based on category
  function getKPIsForCategory(data, categoryName) {
    const b = basis.value
    const d = domain.value

    const kpiMap = {
      energy: [
        { label: 'Energy', value: data?.[`E_${b}`]?.[0], unit: b === 'abs' ? 'Wh' : (b === 'grav' ? 'Wh/kg' : 'Wh/L') },
        { label: 'Capacity', value: data?.[`CAP_${b}`]?.[0], unit: b === 'abs' ? 'Ah' : (b === 'grav' ? 'Ah/kg' : 'Ah/L') },
        { label: 'Temperature', value: data?.T?.[0], unit: '°C' }
      ],
      power: [
        { label: 'Peak Power', value: data?.[`P_peak_${b}`]?.[0], unit: b === 'abs' ? 'W' : (b === 'grav' ? 'W/kg' : 'W/L') },
        { label: 'Peak Current', value: data?.[`I_peak_${b}`]?.[0], unit: b === 'abs' ? 'A' : (b === 'grav' ? 'A/kg' : 'A/L') },
        { label: 'Temperature', value: data?.T?.[0], unit: '°C' }
      ],
      voltage: [
        { label: 'Voltage', value: data?.V?.[0], unit: 'V' },
        { label: 'DCIR', value: data?.R_dc?.[0], unit: 'mΩ' },
        { label: 'Temperature', value: data?.T?.[0], unit: '°C' }
      ],
      resistance: [
        { label: 'DCIR', value: data?.R_dc?.[0], unit: 'mΩ' },
        { label: 'AC Impedance', value: data?.Z_ac?.[0], unit: 'mΩ' },
        { label: 'Temperature', value: data?.T?.[0], unit: '°C' }
      ],
      efficiency: [
        { label: 'DC Efficiency', value: data?.eta_dc?.[0], unit: '%' },
        { label: 'Round-trip Eff', value: data?.eta_rt?.[0], unit: '%' },
        { label: 'Temperature', value: data?.T?.[0], unit: '°C' }
      ]
    }

    return kpiMap[categoryName] || kpiMap.energy
  }

  // Render based on dimension (2D or 3D only - 1D values are in table views)
  // Show second plot for:
  // - Normal categories (energy, power, dc-resistance) in any dimension
  // - AC impedance in 3D mode (Bode plots need 2 charts)
  // - Hide for voltage (always single plot) and AC impedance in 2D mode (Nyquist single plot)
  const showSecondPlot = category === 'voltage' ? false
    : category === 'ac-impedance' ? dimension === '3d'  // Bode needs 2 plots, Nyquist needs 1
    : true  // All other categories show 2 plots

  // AC Impedance and Voltage always use 2D containers (even in "3D" mode for Bode)
  const use2DContainer = dimension === '2d' || category === 'ac-impedance' || category === 'voltage'
  const use3DContainer = dimension === '3d' && category !== 'ac-impedance' && category !== 'voltage'

  // Axis names for the slice captions above the two plots
  const firstSliceData = multiCellData?.[0]?.data
  const sliceAxes = { z: firstSliceData?.zLabel || '', x: firstSliceData?.xLabel || '' }

  return (
    <div className="dimension-view-container">
      {use2DContainer && (
        <div className="dimension-2d">
          <div className={`plot-2d-container${hasLoadPoints ? ' plot-2d-container--with-toolbar' : ''}`}>
            {hasLoadPoints && (
              <div className="plot-slice-caption">
                <span className="plot-slice-caption__axes">{sliceAxes.z} vs {sliceAxes.x}</span>
                <span className="plot-slice-caption__at">at 25 °C ambient</span>
              </div>
            )}
            <div className="plot-2d-chart" ref={plot2dRef1}></div>
          </div>
          {showSecondPlot && (
            <div className={`plot-2d-container${hasLoadPoints ? ' plot-2d-container--with-toolbar' : ''}`}>
              {hasLoadPoints && (
                <div className="load-point-toggle plot-slice-caption" role="group" aria-label="Load point of this plot">
                  <span className="plot-slice-caption__axes">{sliceAxes.z} vs Ambient Temperature</span>
                  <span className="plot-slice-caption__at">at load point</span>
                  {LOAD_POINTS.map(lp => (
                    <button
                      key={lp.key}
                      type="button"
                      className={`load-point-toggle__btn${loadPoint === lp.key ? ' is-active' : ''}`}
                      aria-pressed={loadPoint === lp.key}
                      onClick={() => setLoadPoint(lp.key)}
                    >
                      {lp.label}
                    </button>
                  ))}
                </div>
              )}
              <div className="plot-2d-chart" ref={plot2dRef2}></div>
            </div>
          )}
        </div>
      )}

      {use3DContainer && (
        <div className="dimension-3d">
          <div className="plot-3d-container">
            <div className="plot-3d-chart" ref={plot3dRef}></div>
          </div>
        </div>
      )}
    </div>
  )
}
